import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { SignupWizardProvider } from "@/hooks/useSignupWizard";
import ProtectedRoute from "@/components/ProtectedRoute";
import DomainRouter from "@/components/DomainRouter";
import ArtistIdRedirect from "@/components/ArtistIdRedirect";
import ScrollToTop from "@/components/ScrollToTop";
import LegalUpdateBanner from "@/components/LegalUpdateBanner";
import { useLegalDocumentsCheck } from "@/hooks/useLegalDocumentsCheck";

// Critical path - keep eager loaded for fast initial render
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";

// Lazy load all other routes
const Home = lazy(() => import("./pages/Home"));
const ArtistProfile = lazy(() => import("./pages/ArtistProfile"));
const ArtistsPage = lazy(() => import("./pages/ArtistsPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const GigsPage = lazy(() => import("./pages/GigsPage"));
const VenuesPage = lazy(() => import("./pages/VenuesPage"));
const VenueProfile = lazy(() => import("./pages/VenueProfile"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const FanProfile = lazy(() => import("./pages/FanProfile"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const CreateProfile = lazy(() => import("./pages/CreateProfile"));
const CreateArtist = lazy(() => import("./pages/CreateArtist"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const About = lazy(() => import("./pages/About"));
const Donate = lazy(() => import("./pages/Donate"));
const SignupEntry = lazy(() => import("./pages/signup/SignupEntry"));
const VerifyEmail = lazy(() => import("./pages/signup/VerifyEmail"));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback"));
const FanStep1 = lazy(() => import("./pages/signup/fan/FanStep1"));
const FanStep2 = lazy(() => import("./pages/signup/fan/FanStep2"));
const FanStep3 = lazy(() => import("./pages/signup/fan/FanStep3"));
const FanStep4 = lazy(() => import("./pages/signup/fan/FanStep4"));

const queryClient = new QueryClient();

// Minimal loading fallback that doesn't cause layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Inner component that uses the legal documents hook
const AppContent = () => {
  const { showBanner, pendingDocuments, markAsAccepted } = useLegalDocumentsCheck();

  return (
    <>
      <BrowserRouter>
        {showBanner && (
          <LegalUpdateBanner
            pendingDocuments={pendingDocuments}
            onDismiss={markAsAccepted}
          />
        )}
        <ScrollToTop />
        <DomainRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/artists" element={<ArtistsPage />} />
              <Route path="/artists/:slug" element={<ArtistProfile />} />
              <Route path="/artist/:id" element={<ArtistIdRedirect />} />
              <Route path="/bands" element={<Navigate to="/artists" replace />} />
              <Route path="/band/:id" element={<Navigate to="/artists" replace />} />
              <Route path="/gigs" element={<GigsPage />} />
              <Route path="/venues" element={<VenuesPage />} />
              <Route path="/venues/:slug" element={<VenueProfile />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/fan/:username" element={<FanProfile />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsConditions />} />
              <Route path="/about" element={<About />} />
              <Route path="/donate" element={<Donate />} />
              
              
              
              {/* Auth routes */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* Signup routes - Simplified flow */}
              <Route path="/signup" element={<Navigate to="/signup/fan/step1" replace />} />
              <Route path="/signup/verify-email" element={<VerifyEmail />} />
              <Route path="/signup/fan/step1" element={<FanStep1 />} />
              <Route path="/signup/fan/step2" element={<FanStep2 />} />
              <Route path="/signup/fan/step3" element={<FanStep3 />} />
              <Route path="/signup/fan/step4" element={<FanStep4 />} />
              
              {/* Legacy artist signup routes - redirect to fan signup */}
              <Route path="/signup/artist/*" element={<Navigate to="/signup/fan/step1" replace />} />
              
              {/* Auth callback */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              
              {/* Protected routes */}
              <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              <Route path="/me" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
              <Route path="/create-profile" element={<ProtectedRoute><CreateProfile /></ProtectedRoute>} />
              <Route path="/create-artist" element={<ProtectedRoute><CreateArtist /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </DomainRouter>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SignupWizardProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AppContent />
          </TooltipProvider>
        </SignupWizardProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
